import type { RefObject } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyGroupResponse } from '@/shared/api';
import { STRUCTURE_BLOCK_REASON, blockFor } from './objectStructure';
import type { StructureAction } from './useObjectStructure';

const ACTIONS: { action: StructureAction; label: string; description: string }[] = [
  {
    action: 'merge',
    label: 'Zusammenführen',
    description: 'Zwei Objekte sind in Wahrheit dasselbe Haus.',
  },
  { action: 'split', label: 'Teilen', description: 'Ein Objekt sind in Wahrheit zwei Häuser.' },
  { action: 'archive', label: 'Archivieren', description: 'Das Objekt wird nicht mehr gebraucht.' },
];

interface Props {
  group: PropertyGroupResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoose: (action: StructureAction) => void;
  /** Der Aktionen-Knopf: die Auswahl hat keinen eigenen Auslöser, zu dem der Fokus zurückkehrt. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * Wählt den Weg, bevor sein Dialog aufgeht. Eine Sperre steht **vor** dem Knopf und nicht als
 * Fehler danach: ein Knopf, den der Server mit 409 beantwortet, wäre dieselbe Lüge wie einer,
 * der ein 403 kassiert.
 */
export function ObjectStructureChooser({
  group,
  open,
  onOpenChange,
  onChoose,
  returnFocusTo,
}: Props) {
  const published = blockFor(group, 'merge') === 'published';
  const archiveBlocked = blockFor(group, 'archive');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        onCloseAutoFocus={(event) => {
          const target = returnFocusTo?.current;
          if (!target?.isConnected) return;
          event.preventDefault();
          target.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Objektstruktur ändern</DialogTitle>
          <DialogDescription>
            Zusammenführen, Teilen und Archivieren verändern, welche Apartments zu diesem Objekt
            gehören.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {published && (
            <p className="border-line/60 bg-card-alt/40 text-ink-soft rounded-md border p-3 text-sm">
              {STRUCTURE_BLOCK_REASON.published}
            </p>
          )}
          <ul className="divide-line/60 border-line/60 divide-y overflow-hidden rounded-md border">
            {ACTIONS.map(({ action, label, description }) => (
              <li key={action}>
                <button
                  type="button"
                  disabled={!!blockFor(group, action)}
                  onClick={() => onChoose(action)}
                  className="hover:bg-card-alt/60 focus-visible:ring-sage flex min-h-14 w-full items-center gap-3 px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  <span className="min-w-0 flex-1">
                    <span className="text-ink block text-sm font-semibold">{label}</span>
                    <span className="text-ink-soft block text-xs">{description}</span>
                  </span>
                  <ChevronRight size={16} className="text-ink-faint shrink-0" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
          {!published && archiveBlocked === 'not-empty' && (
            <p className="text-ink-soft text-xs">{STRUCTURE_BLOCK_REASON['not-empty']}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => onOpenChange(false)}
          >
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
